<template>
  <div class="tw-font-geist">
    <!-- Slot for filters -->
    <div
      class="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center sm:tw-px-2 tw-gap-3 md:tw-gap-0 tw-pt-2 tw-pb-5 tw-flex-wrap"
    >
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full sm:tw-w-auto"
      >
        <slot name="filters"></slot>
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full sm:tw-w-auto tw-justify-end"
      >
        <slot name="custom-filters"></slot>
        <DateMenu @set-date-range="setDateRange" />
      </div>
    </div>

    <!-- Common Cards -->
    <div class="tw-flex tw-gap-2 tw-flex-wrap">
      <OverviewCard
        v-if="showOverviewPlaceholder && !loadingOverviews"
        icon="pi-eye"
        title="Overview"
        :placeholder="true"
        placeholder-message="Your metrics will be here"
        :active-card="true"
        :clickable="false"
      />
      <template v-else>
        <OverviewCard
          v-for="overview in overviews"
          :key="overview.type"
          :icon="overview.icon"
          :title="overview.title"
          :value="overview.total"
          :second-value="overview.diff"
          :loading="loadingOverviews"
          :active-card="overview.type === overtimeEventType"
          :clickable="overview.clickable"
          :tooltip="overview.tooltip"
          :class="[loadingOverviews && 'tw-pointer-events-none']"
          @click="
            handleActiveOverviewCard(
              overview.type,
              overview.status,
              overview.clickable
            )
          "
        />
      </template>
    </div>

    <!-- Overtime reach -->
    <div class="tw-mt-4">
      <OvertimeCard
        :data="overtimeData"
        :loading="loadingOvertimeData"
        :title="overtimeType"
      />
    </div>

    <!-- Countries and Devices-->
    <div
      class="tw-mt-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-items-stretch"
    >
      <TopCountriesCard
        :data="topByCountries"
        :loading="loadingTopByCountries"
        :type="type"
      />
      <ByDeviceCard :data="topByDevices" :loading="loadingTopByDevices" />
    </div>

    <!-- Slot for tables -->
    <div class="tw-mt-4">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, defineProps } from "vue";
import moment from "moment";
import axios from "axios";
import { API_BASE_URL } from "@/consts";
import OverviewCard from "@/components/podcastadmin_dashboard_new/components/OverviewCard.vue";
import OvertimeCard from "@/components/podcastadmin_dashboard_new/components/OvertimeCard.vue";
import TopCountriesCard from "@/components/podcastadmin_dashboard_new/components/TopCountriesCard.vue";
import ByDeviceCard from "@/components/podcastadmin_dashboard_new/components/ByDeviceCard.vue";
import DateMenu from "@/components/podcastadmin_dashboard_new/components/DateMenu.vue";

const props = defineProps({
  baseApiParams: { type: Object, required: true },
  authToken: { type: String, required: true },
  isRevenueClickable: { type: Boolean, default: true },
});

// --- Unificated API ---
const apiEndpoints = {
  totals: "/podcast_events/totals",
  overTime: "/podcast_events/over-time",
  topByCountries: "/podcast_events/top-by-countries",
  byDeviceType: "/podcast_events/by-device-type",
};

// --- State ---
const loadingOverviews = ref(false);
const loadingOvertimeData = ref(false);
const loadingTopByCountries = ref(false);
const loadingTopByDevices = ref(false);
const showOverviewPlaceholder = ref(false);

const dates = ref([new Date(moment().subtract(1, "week")), new Date(moment())]);
const overtimeData = ref([]);
const topByCountries = ref([]);
const topByDevices = ref([]);
const eventType = ref("episode_imp");
const overtimeEventType = ref("episode_imp");

// --- Configs ---
const overviewConfig = computed(() => [
  {
    icon: "pi-eye",
    title: "Views",
    key: "total_views",
    type: "episode_imp",
    clickable: true,
    status: true,
  },
  {
    icon: "pi-headphones",
    title: "Unique Listeners",
    key: "total_unique_listeners",
    type: "",
    clickable: false,
  },
  {
    icon: "pi-percentage",
    title: "CTR",
    key: "overall_ctr",
    type: "",
    clickable: false,
    isPercent: true,
  },
  {
    icon: "pi-bullseye",
    title: "Impressions",
    key: "impressions",
    type: "impressions",
    clickable: true,
  },
  {
    icon: "pi-file-excel",
    title: "ECPM",
    key: "ecpm",
    type: "",
    clickable: false,
    isFixed: 2,
  },
  {
    icon: "pi-dollar",
    title: "Estimated Revenue",
    key: "revenue",
    type: "revenue",
    clickable: props.isRevenueClickable,
    isCurrency: true,
    tooltip:
      "The revenue figures mentioned are estimated and may be subject to adjustment based on actual performance.",
  },
]);

const overviews = ref(
  overviewConfig.value.map((config) => ({ ...config, total: "0", diff: "N/A" }))
);

// --- Helpers ---
const formatNumber = (num) => {
  const value = Number(num);
  if (isNaN(value)) return num;
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (value >= 1_000) return (value / 1_000).toFixed(1) + "K";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const generateDiff = (current, past) => {
  const currNum = parseFloat(current);
  const pastNum = parseFloat(past);
  if (isNaN(currNum) || isNaN(pastNum) || pastNum === 0) return "N/A";
  const diff = ((currNum - pastNum) / pastNum) * 100;
  return `${diff > 0 ? "+" : ""}${diff.toFixed(2)}%`;
};

const type = computed(() =>
  eventType.value === "episode_imp" ? "Reach" : "Sessions"
);
const overtimeType = computed(
  () =>
    ({
      episode_imp: "Reach",
      episode_play: "Sessions",
      impressions: "Impressions",
      revenue: "Revenue",
    }[overtimeEventType.value] || "Reach")
);

// --- handlers ---
const setDateRange = (dateRange) => {
  dates.value = dateRange;
};
const handleActiveOverviewCard = (type, status, clickable) => {
  if (!clickable) return;
  overtimeEventType.value = type;
  if (status && eventType.value !== type) {
    eventType.value = type;
    fetchTopCountries();
    fetchTopDevices();
  }
};

// --- Loading data ---
const getCommonParams = () => ({
  ...props.baseApiParams,
  startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
  endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
});
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${props.authToken}` },
});

const fetchAllData = () => {
  if (dates.value && dates.value[0] && dates.value[1]) {
    fetchOverviews();
    fetchOvertime();
    fetchTopCountries();
    fetchTopDevices();
  }
};

const fetchOverviews = async () => {
  loadingOverviews.value = true;
  try {
    const { data } = await axios.get(`${API_BASE_URL}${apiEndpoints.totals}`, {
      params: getCommonParams(),
      ...getAuthHeaders(),
    });
    if (data) {
      const hasNonZeroMetric = overviewConfig.value.some((cfg) => {
        const numericValue = Number(data[cfg.key]);
        return !Number.isNaN(numericValue) && numericValue !== 0;
      });
      overviews.value = overviewConfig.value.map((cfg) => {
        let total;
        const val = data[cfg.key];
        if (cfg.isPercent) total = val ? `${(val * 100).toFixed(1)}%` : `0%`;
        else if (cfg.isFixed) total = val ? `${val.toFixed(cfg.isFixed)}` : `0`;
        else if (cfg.isCurrency) total = `$${formatNumber(val)}`;
        else total = formatNumber(val);
        return {
          ...cfg,
          total:
            cfg.title === "Estimated Revenue" && !props.isRevenueClickable
              ? "N/A"
              : total,
          diff: generateDiff(val, data.pastTotals?.[cfg.key]) || "N/A",
        };
      });
      showOverviewPlaceholder.value = !hasNonZeroMetric;
    }
  } catch (error) {
    console.error("Error fetching overviews:", error);
  } finally {
    setTimeout(() => (loadingOverviews.value = false), 300);
  }
};

const fetchOvertime = async () => {
  loadingOvertimeData.value = true;
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}${apiEndpoints.overTime}`,
      { params: getCommonParams(), ...getAuthHeaders() }
    );
    if (data) overtimeData.value = data.over_time_data;
  } catch (error) {
    console.error("Error fetching overtime data:", error);
  } finally {
    setTimeout(() => (loadingOvertimeData.value = false), 300);
  }
};

const fetchTopCountries = async () => {
  loadingTopByCountries.value = true;
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}${apiEndpoints.topByCountries}`,
      {
        params: { ...getCommonParams(), eventTypes: eventType.value },
        ...getAuthHeaders(),
      }
    );
    if (data) topByCountries.value = data.top_by_countries;
  } catch (err) {
    console.error("Error fetching top countries:", err);
  } finally {
    setTimeout(() => (loadingTopByCountries.value = false), 300);
  }
};

const fetchTopDevices = async () => {
  loadingTopByDevices.value = true;
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}${apiEndpoints.byDeviceType}`,
      {
        params: { ...getCommonParams(), eventTypes: eventType.value },
        ...getAuthHeaders(),
      }
    );
    if (data) topByDevices.value = data.by_device_type;
  } catch (err) {
    console.error("Error fetching top devices:", err);
  } finally {
    setTimeout(() => (loadingTopByDevices.value = false), 300);
  }
};

watch([dates, () => props.baseApiParams], fetchAllData, {
  deep: true,
  immediate: true,
});
</script>
